import type { EmployeeConfigView, EmployeeUsageResponse } from '@omni/shared';
import { useEffect, useState } from 'react';
import { useSession } from '../app/SessionProvider';
import { Button, Card, Field, TextInput } from '../components/ui';
import { zh } from '../i18n';
import { formatUsd } from '../lib/format';

/**
 * 費用上限 (SPEC §10, CONTRACTS §4.1 / §4.2).
 *
 * The rule this section exists to obey: **no budget number is rendered without
 * consulting `budgetsEnforced`.** Under an `oauth_token` credential the SDK
 * reports no dollars, so `maxBudgetUsd` can never trip and the gateway's daily
 * gate sums zeros — the ceilings are stored but inert. Drawing them as live
 * controls, or drawing a `$0.00 / $10.00` meter, would be a limit the product
 * cannot enforce.
 *
 * So when the flag is false: the two dollar fields are disabled, the frozen
 * 繁中 notice is shown, and the usage meter is replaced by 「訂閱額度制」. The
 * bound that DOES apply under both credential kinds — `maxTurns` — stays
 * editable, and says so.
 *
 * The flag comes from `useSession()` (stream-first), not from the employee
 * snapshot, so a `credential.changed` event re-enables the fields immediately.
 */

export interface BudgetSectionProps {
  readonly employee: EmployeeConfigView;
  readonly usage: EmployeeUsageResponse | null;
  readonly onSave: (patch: { maxTurns?: number; maxBudgetUsd?: number }) => void;
  readonly saving: boolean;
}

export function BudgetSection({
  employee,
  usage,
  onSave,
  saving,
}: BudgetSectionProps): JSX.Element {
  const { budgetsEnforced } = useSession();
  const [maxTurns, setMaxTurns] = useState(String(employee.maxTurns));
  const [maxBudget, setMaxBudget] = useState(String(employee.maxBudgetUsd));

  useEffect(() => {
    setMaxTurns(String(employee.maxTurns));
    setMaxBudget(String(employee.maxBudgetUsd));
  }, [employee.maxTurns, employee.maxBudgetUsd]);

  const dirty =
    Number(maxTurns) !== employee.maxTurns || Number(maxBudget) !== employee.maxBudgetUsd;

  const todayCost = usage?.todayCostUsd ?? 0;
  const dailyLimit = usage?.dailyBudgetUsd ?? employee.dailyBudgetUsd;
  const meterPct = dailyLimit > 0 ? Math.min(100, (todayCost / dailyLimit) * 100) : 0;

  return (
    <Card>
      <h2>{zh.settings.sectionBudget}</h2>

      {!budgetsEnforced ? (
        <div className="banner banner--warn" role="status">
          <span className="banner__text">
            <strong>{zh.settings.budgetsDisabledNotice}</strong>
            <br />
            <span className="tiny">{zh.settings.budgetsDisabledDetail}</span>
          </span>
        </div>
      ) : null}

      <div className="stack">
        <Field label={zh.settings.maxTurns} hint={zh.settings.maxTurnsHint}>
          {(id) => (
            <TextInput
              id={id}
              type="number"
              min={1}
              max={200}
              inputMode="numeric"
              value={maxTurns}
              onChange={(event) => setMaxTurns(event.target.value)}
            />
          )}
        </Field>

        <Field
          label={zh.settings.maxBudget}
          hint={
            budgetsEnforced
              ? zh.settings.budgetCeiling(formatUsd(employee.maxBudgetCeilingUsd))
              : zh.settings.budgetsDisabledNotice
          }
        >
          {(id) => (
            <TextInput
              id={id}
              type="number"
              min={0}
              step="0.01"
              inputMode="decimal"
              value={maxBudget}
              disabled={!budgetsEnforced}
              onChange={(event) => setMaxBudget(event.target.value)}
            />
          )}
        </Field>

        <Field
          label={zh.settings.dailyBudget}
          hint={budgetsEnforced ? zh.settings.dailyBudgetHint : zh.settings.budgetsDisabledNotice}
        >
          {(id) => (
            <TextInput
              id={id}
              type="number"
              value={String(dailyLimit)}
              disabled
              readOnly
              aria-disabled={!budgetsEnforced}
            />
          )}
        </Field>

        <div>
          <div className="row row--between">
            <span className="small muted">{zh.settings.usageTitle}</span>
            {budgetsEnforced ? (
              <span className="small">{zh.cost.todayCost(formatUsd(todayCost))}</span>
            ) : (
              <span className="badge badge--muted">{zh.cost.subscriptionMeter}</span>
            )}
          </div>
          {budgetsEnforced ? (
            <div className="meter" aria-hidden="true">
              <div
                className={meterPct > 90 ? 'meter__fill meter__fill--danger' : 'meter__fill'}
                style={{ width: `${meterPct}%` }}
              />
            </div>
          ) : null}
        </div>

        {/* Per-day history. The cost column only exists when dollars do. */}
        {usage && usage.days.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th>{zh.settings.usageDay}</th>
                <th>{zh.settings.usageTurns}</th>
                <th>{zh.settings.usageCost}</th>
              </tr>
            </thead>
            <tbody>
              {usage.days.map((day) => (
                <tr key={day.date}>
                  <td className="mono">{day.date}</td>
                  <td>{day.turns}</td>
                  <td>
                    {budgetsEnforced ? formatUsd(day.costUsd) : zh.cost.subscriptionMeter}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="small muted">{zh.settings.usageEmpty}</p>
        )}

        <Button
          variant="primary"
          disabled={saving || !dirty}
          onClick={() =>
            onSave({
              maxTurns: Number(maxTurns),
              ...(budgetsEnforced ? { maxBudgetUsd: Number(maxBudget) } : {}),
            })
          }
        >
          {saving ? zh.app.saving : zh.app.save}
        </Button>
      </div>
    </Card>
  );
}
